import React, { useState, useEffect, useCallback } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import { useParams } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Flex,
  Spinner,
  Badge,
  Input,
  Textarea,
  RadioGroup,
  Checkbox,
  Image,
  SimpleGrid,
  Alert,
  Heading,
  Card,
  Icon,
  Stack,
  Separator,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import { Radio } from '@/components/ui/chakra-v2-compat';
import { CheckboxGroup } from '@/components/ui/multi-checkbox-field';
import { toaster } from '@/components/ui/toaster';
import { Avatar } from '@/components/ui/avatar';
import { FaCheck, FaLock, FaEyeSlash } from 'react-icons/fa';
import { encuestasPublicasAPI } from '../services/api';
import { resolveUploadsUrl } from '../utils/staticUrl';

const EncuestaConsultaPublica = () => {
  const { shareToken, convocadoToken: convocadoTokenParam } = useParams();
  

  const [mode, setMode] = useState('loading');
  const [encuestaMeta, setEncuestaMeta] = useState(null);
  const [encuestaFull, setEncuestaFull] = useState(null);
  const [musicosList, setMusicosList] = useState([]);
  const [searchFilter, setSearchFilter] = useState('');
  const [selectedMusico, setSelectedMusico] = useState(null);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [hasPassword, setHasPassword] = useState(false);
  const [convocadoToken, setConvocadoToken] = useState(convocadoTokenParam || '');
  const [respuestas, setRespuestas] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const bgPage = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.700');
  const subtleBg = useColorModeValue('gray.50', 'gray.600');
  const accentColor = useColorModeValue('purple.600', 'purple.300');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headerBg = useColorModeValue('purple.600', 'purple.700');

  // ── Init: decide flow based on URL params ───────────────────────────────
  useEffect(() => {
    if (convocadoTokenParam) {
      loadEncuestaDirecta(convocadoTokenParam);
    } else if (shareToken) {
      loadMeta(shareToken);
    } else {
      setMode('error');
      setErrorMsg('Enlace no válido.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── API calls ───────────────────────────────────────────────────────────
  const loadMeta = async (token) => {
    try {
      const { data } = await encuestasPublicasAPI.getMeta(token);
      setEncuestaMeta(data);
      if (data.estado === 'CERRADA') {
        setMode('closed');
      } else {
        const musicos = await encuestasPublicasAPI.getMusicos(token);
        setHasPassword(musicos.data.hasPassword);
        setMusicosList(musicos.data.convocados || []);
        setMode('identify');
      }
    } catch (err) {
      setMode('error');
      setErrorMsg(
        err.response?.status === 404
          ? 'Encuesta no encontrada o el enlace ha expirado.'
          : 'Error al cargar la encuesta. Inténtalo de nuevo.',
      );
    }
  };

  const loadEncuestaDirecta = async (token) => {
    try {
      const { data } = await encuestasPublicasAPI.getEncuesta(token);
      setEncuestaFull(data);
      setConvocadoToken(token);

      if (data.estado === 'CERRADA') {
        setMode('closed');
        return;
      }

      if (data.yaRespondido) {
        setMode('already');
        return;
      }

      initRespuestas(data.preguntas);
      setMode('form');
    } catch (err) {
      setMode('error');
      setErrorMsg(
        err.response?.status === 404
          ? 'Enlace no válido o expirado.'
          : 'Error al cargar la encuesta.',
      );
    }
  };

  const handleVerify = async () => {
    if (hasPassword && !password.trim()) {
      setPasswordError('Introduce la contraseña.');
      return;
    }
    setVerifying(true);
    setPasswordError('');
    try {
      const { data } = await encuestasPublicasAPI.verificar(
        shareToken,
        selectedMusico.id,
        password,
      );
      const token = data.convocadoToken;
      setConvocadoToken(token);
      await loadEncuestaDirecta(token);
    } catch (err) {
      const msg =
        err.response?.data?.message || err.response?.data?.error || 'Contraseña incorrecta.';
      setPasswordError(msg);
    } finally {
      setVerifying(false);
    }
  };

  // ── Respuestas helpers ──────────────────────────────────────────────────
  const initRespuestas = (preguntas, previas = null) => {
    const initial = {};
    (preguntas || []).forEach((p) => {
      if (previas && previas[p.id]) {
        initial[p.id] = {
          textoLibre: previas[p.id].textoLibre || '',
          opcionIds: previas[p.id].opcionIds || [],
        };
      } else {
        initial[p.id] = { textoLibre: '', opcionIds: [] };
      }
    });
    setRespuestas(initial);
  };

  const updateRespuesta = useCallback((preguntaId, field, value) => {
    setRespuestas((prev) => ({
      ...prev,
      [preguntaId]: { ...prev[preguntaId], [field]: value },
    }));
  }, []);

  const handleSubmit = async () => {
    const preguntas = encuestaFull?.preguntas || [];
    const missing = preguntas.filter((p) => {
      if (!p.obligatoria) return false;
      const r = respuestas[p.id];
      if (!r) return true;
      if (p.tipoPregunta === 'TEXTO_LIBRE') return !r.textoLibre?.trim();
      return !r.opcionIds?.length;
    });

    if (missing.length > 0) {
      toaster.create({
        title: 'Faltan respuestas obligatorias',
        description: `Por favor responde a las preguntas: ${missing
          .map((p) => `#${p.orden}`)
          .join(', ')}`,
        type: 'warning',
        duration: 5000,
        closable: true,
      });
      return;
    }

    setSubmitting(true);
    try {
      const payload = Object.entries(respuestas).map(([preguntaId, val]) => ({
        preguntaId,
        textoLibre: val.textoLibre || null,
        opcionIds: val.opcionIds || [],
      }));
      await encuestasPublicasAPI.responder(convocadoToken, payload);
      setMode('submitted');
    } catch (err) {
      toaster.create({
        title: 'Error al enviar',
        description:
          err.response?.data?.message || 'No se pudieron guardar las respuestas.',
        type: 'error',
        duration: 5000,
        closable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Filtered musicians ──────────────────────────────────────────────────
  const filteredMusicos = musicosList.filter((c) => {
    const term = searchFilter.toLowerCase();
    const nombre = (c.musico?.nombre || '').toLowerCase();
    const apellidos = (c.musico?.apellidos || '').toLowerCase();
    return nombre.includes(term) || apellidos.includes(term);
  });

  // ── Render helpers ──────────────────────────────────────────────────────

  const pageShell = (content) => (
    <Box minH="100vh" bg={bgPage}>
      <Flex direction="column" minH="100vh">
        <Box flex="1" py={{ base: 6, md: 10 }} px={{ base: 4, md: 6 }}>
          <Box maxW="700px" mx="auto">
            {content}
          </Box>
        </Box>
        <Flex justify="center" py={4} opacity={0.5}>
          <Text fontSize="xs" color="gray.500">
            Powered by <strong>Cadencya</strong>
          </Text>
        </Flex>
      </Flex>
    </Box>
  );

  // ── MODE: loading ───────────────────────────────────────────────────────
  if (mode === 'loading') {
    return pageShell(
      <Flex justify="center" align="center" minH="300px">
        <VStack gap={4}>
          <Spinner size="xl" color="purple.500" borderWidth="4px" />
          <Text color="gray.500">Cargando encuesta…</Text>
        </VStack>
      </Flex>
    );
  }

  // ── MODE: error ─────────────────────────────────────────────────────────
  if (mode === 'error') {
    return pageShell(
      <Card.Root bg={cardBg} shadow="lg" borderRadius="xl" overflow="hidden">
        <Card.Body>
          <VStack gap={5} py={6} textAlign="center">
            <Icon boxSize={12} color="red.400" asChild><FaLock /></Icon>
            <Heading size="md" color="red.500">
              Enlace no disponible
            </Heading>
            <Text color="gray.600">{errorMsg}</Text>
          </VStack>
        </Card.Body>
      </Card.Root>
    );
  }

  // ── MODE: closed ────────────────────────────────────────────────────────
  if (mode === 'closed') {
    return pageShell(
      <Card.Root bg={cardBg} shadow="lg" borderRadius="xl" overflow="hidden">
        <Card.Body>
          <VStack gap={5} py={6} textAlign="center">
            <Icon boxSize={12} color="orange.400" asChild><FaLock /></Icon>
            <Heading size="md">Esta encuesta está cerrada</Heading>
            <Text color="gray.500">
              Ya no se aceptan más respuestas. Gracias por tu interés.
            </Text>
          </VStack>
        </Card.Body>
      </Card.Root>
    );
  }

  if (mode === 'already') {
    return pageShell(
      <Card.Root bg={cardBg} shadow="lg" borderRadius="xl" overflow="hidden">
        <Card.Body>
          <VStack gap={5} py={8} textAlign="center">
            <Flex
              w={20} h={20} borderRadius="full"
              bg="green.100" align="center" justify="center"
            >
              <Icon boxSize={10} color="green.500" asChild><FaCheck /></Icon>
            </Flex>
            <Heading size="lg" color={accentColor}>
              Ya has respondido
            </Heading>
            <Text color="gray.600" fontSize="lg">
              Tu voto ya está registrado. No se puede volver a votar en esta encuesta.
            </Text>
          </VStack>
        </Card.Body>
      </Card.Root>
    );
  }

  // ── MODE: submitted ─────────────────────────────────────────────────────
  if (mode === 'submitted') {
    return pageShell(
      <Card.Root bg={cardBg} shadow="lg" borderRadius="xl" overflow="hidden">
        <Card.Body>
          <VStack gap={5} py={8} textAlign="center">
            <Flex
              w={20} h={20} borderRadius="full"
              bg="green.100" align="center" justify="center"
            >
              <Icon boxSize={10} color="green.500" asChild><FaCheck /></Icon>
            </Flex>
            <Heading size="lg" color={accentColor}>
              ¡Gracias!
            </Heading>
            <Text color="gray.600" fontSize="lg">
              Tus respuestas han sido enviadas correctamente.
            </Text>
          </VStack>
        </Card.Body>
      </Card.Root>
    );
  }

  // ── MODE: identify (select musician + PIN) ──────────────────────────────
  if (mode === 'identify' || mode === 'pin') {
    return pageShell(
      <>
        {/* Header card — logo + nombre de la entidad */}
        <Card.Root bg={cardBg} shadow="lg" borderRadius="xl" overflow="hidden" mb={6}>
          <Box bg={headerBg} px={6} py={6}>
            <VStack gap={3} align="center" textAlign="center">
              <Avatar.Root
                size="lg"
                bg="white"
                color="purple.700"
                border="2px solid"
                borderColor="whiteAlpha.800"
              >
                <Avatar.Fallback name={encuestaMeta?.bandaNombre || 'Banda'} />
                <Avatar.Image src={encuestaMeta?.bandaAvatar || undefined} />
              </Avatar.Root>
              <VStack gap={1} align="center" maxW="100%">
                {encuestaMeta?.bandaNombre && (
                  <Text color="white" fontSize="sm" fontWeight="semibold" lineClamp={2}>
                    {encuestaMeta.bandaNombre}
                  </Text>
                )}
                <Heading size="md" color="white" lineClamp={2}>
                  {encuestaMeta?.titulo || 'Encuesta'}
                </Heading>
              </VStack>
            </VStack>
          </Box>
          {encuestaMeta?.introduccion && (
            <Card.Body>
              <Text color="gray.600" whiteSpace="pre-line">
                {encuestaMeta.introduccion}
              </Text>
            </Card.Body>
          )}
        </Card.Root>

        {/* Step: select musician */}
        {!selectedMusico ? (
          <Card.Root bg={cardBg} shadow="md" borderRadius="xl" overflow="hidden">
            <Card.Body>
              <VStack gap={4} align="stretch">
                <Heading size="sm" color={accentColor}>
                  Identifícate para continuar
                </Heading>
                <Text fontSize="sm" color="gray.500">
                  Selecciona tu nombre de la lista de músicos convocados.
                </Text>

                <Input
                  placeholder="Buscar por nombre…"
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                  size="md"
                  borderRadius="lg" />

                <Box
                  maxH="320px" overflowY="auto"
                  border="1px solid" borderColor={borderColor} borderRadius="lg"
                >
                  {filteredMusicos.length === 0 ? (
                    <Text textAlign="center" py={6} color="gray.400" fontSize="sm">
                      No se encontraron músicos
                    </Text>
                  ) : (
                    <VStack gap={0} align="stretch">{filteredMusicos.map((c) => {
                        const nombre = c.musico?.nombre || '';
                        const apellidos = c.musico?.apellidos || '';
                        const foto = c.musico?.foto;
                        const fotoUrl = foto ? resolveUploadsUrl(foto) : null;
                        return (
                          <Box
                            key={c.id}
                            px={4} py={3}
                            cursor="pointer"
                            _hover={{ bg: 'purple.50' }}
                            transition="background 0.15s"
                            onClick={() => {
                              setSelectedMusico({ ...c, nombre, apellidos, fotoUrl });
                              setMode('pin');
                              setPassword('');
                              setPasswordError('');
                            }}
                          >
                            <HStack gap={3}>
                              {fotoUrl ? (
                                <Image
                                  src={fotoUrl} boxSize="40px"
                                  borderRadius="full" objectFit="cover"
                                />
                              ) : (
                                <Flex
                                  w="40px" h="40px" borderRadius="full"
                                  bg="purple.100" align="center" justify="center"
                                  fontSize="sm" fontWeight="bold" color="purple.600"
                                >
                                  {(nombre || '?')[0]}
                                </Flex>
                              )}
                              <Text fontWeight="medium" fontSize="sm">
                                {nombre} {apellidos}
                              </Text>
                            </HStack>
                          </Box>
                        );
                      })}</VStack>
                  )}
                </Box>
              </VStack>
            </Card.Body>
          </Card.Root>
        ) : (
          /* Step: confirm musician + password */
          <Card.Root bg={cardBg} shadow="md" borderRadius="xl" overflow="hidden">
            <Card.Body>
              <VStack gap={5} align="stretch">
                <HStack gap={3}>
                  <Button
                    variant="ghost" size="sm"
                    onClick={() => {
                      setSelectedMusico(null);
                      setMode('identify');
                      setPassword('');
                      setPasswordError('');
                    }}
                  >
                    ← Volver
                  </Button>
                </HStack>

                <VStack gap={2} textAlign="center">
                  <Text fontSize="sm" fontWeight="600" color={accentColor}>
                    ¿Eres esta persona?
                  </Text>
                  <HStack justify="center" gap={3}>
                    {selectedMusico.fotoUrl ? (
                      <Image
                        src={selectedMusico.fotoUrl} boxSize="56px"
                        borderRadius="full" objectFit="cover"
                        border="3px solid"
                        borderColor="purple.200"
                      />
                    ) : (
                      <Flex
                        w="56px" h="56px" borderRadius="full"
                        bg="purple.100" align="center" justify="center"
                        fontSize="xl" fontWeight="bold" color="purple.600"
                      >
                        {(selectedMusico.nombre || '?')[0]}
                      </Flex>
                    )}
                    <Text fontWeight="bold" fontSize="lg">
                      {selectedMusico.nombre} {selectedMusico.apellidos}
                    </Text>
                  </HStack>
                  <Text fontSize="xs" color="gray.500">
                    Vas a acceder como esta persona. Si no eres tú, pulsa «Volver» y selecciona tu nombre.
                  </Text>
                </VStack>

                {hasPassword ? (
                  <VStack gap={3} textAlign="center">
                    <Text fontSize="sm" color="gray.600">
                      Introduce la contraseña de la encuesta
                    </Text>
                    <Input
                      type="password"
                      placeholder="Contraseña"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setPasswordError('');
                      }}
                      size="lg"
                      maxW="300px"
                      mx="auto"
                      textAlign="center"
                      borderRadius="lg" />

                    {passwordError && (
                      <Text color="red.500" fontSize="sm" fontWeight="medium">
                        {passwordError}
                      </Text>
                    )}

                    <Button
                      colorPalette="purple"
                      size="lg"
                      borderRadius="lg"
                      w="full"
                      maxW="260px"
                      loading={verifying}
                      loadingText="Verificando…"
                      onClick={handleVerify}
                      disabled={!password.trim()}><Icon asChild><FaLock /></Icon>Acceder
                                          </Button>
                  </VStack>
                ) : (
                  <VStack gap={3} textAlign="center">
                    {passwordError && (
                      <Text color="red.500" fontSize="sm" fontWeight="medium">
                        {passwordError}
                      </Text>
                    )}
                    <Button
                      colorPalette="purple"
                      size="lg"
                      borderRadius="lg"
                      w="full"
                      maxW="260px"
                      loading={verifying}
                      loadingText="Accediendo…"
                      onClick={handleVerify}><Icon asChild><FaCheck /></Icon>Sí, soy yo
                                          </Button>
                  </VStack>
                )}
              </VStack>
            </Card.Body>
          </Card.Root>
        )}
      </>
    );
  }

  // ── MODE: form ──────────────────────────────────────────────────────────
  if (mode === 'form' && encuestaFull) {
    const preguntas = [...(encuestaFull.preguntas || [])].sort(
      (a, b) => (a.orden ?? 0) - (b.orden ?? 0),
    );

    return pageShell(
      <>
        {/* Form header — logo + nombre de la entidad + título */}
        <Card.Root bg={cardBg} shadow="lg" borderRadius="xl" overflow="hidden" mb={6}>
          <Box bg={headerBg} px={6} py={6}>
            <VStack gap={4} align="center" textAlign="center">
              <Avatar.Root
                size="xl"
                bg="white"
                color="purple.700"
                border="3px solid"
                borderColor="whiteAlpha.800"
                shadow="md"
              >
                <Avatar.Fallback name={encuestaFull.bandaNombre || 'Banda'} />
                <Avatar.Image src={encuestaFull.bandaAvatar || undefined} />
              </Avatar.Root>
              <VStack gap={1} align="center" maxW="100%">
                {encuestaFull.bandaNombre && (
                  <Text
                    color="white"
                    fontSize="md"
                    fontWeight="semibold"
                    lineClamp={2}
                  >
                    {encuestaFull.bandaNombre}
                  </Text>
                )}
                <Heading size="lg" color="white" lineClamp={3}>
                  {encuestaFull.titulo}
                </Heading>
              </VStack>
            </VStack>
          </Box>
        </Card.Root>

        {/* Anónima alert */}
        {encuestaFull.anonima && (
          <Alert.Root
            status="info" borderRadius="xl" mb={5}
            bg="purple.50" border="1px solid" borderColor="purple.200"
          >
            <Icon color="purple.500" mr={3} boxSize={5} asChild><FaEyeSlash /></Icon>
            <Text fontSize="sm" color="purple.800">
              Esta encuesta es anónima. Tus respuestas no serán asociadas a tu nombre.
            </Text>
          </Alert.Root>
        )}

        {/* Introducción */}
        {encuestaFull.introduccion && (
          <Card.Root bg={subtleBg} borderRadius="xl" mb={5} variant="outline">
            <Card.Body>
              <Text whiteSpace="pre-line" color="gray.700" fontSize="sm">
                {encuestaFull.introduccion}
              </Text>
            </Card.Body>
          </Card.Root>
        )}

        {/* Preguntas */}
        <VStack gap={5} align="stretch">
          {preguntas.map((pregunta, idx) => (
            <PreguntaCard
              key={pregunta.id}
              pregunta={pregunta}
              numero={idx + 1}
              respuesta={respuestas[pregunta.id] || { textoLibre: '', opcionIds: [] }}
              onUpdate={updateRespuesta}
              cardBg={cardBg}
              accentColor={accentColor}
            />
          ))}
        </VStack>

        {/* Submit */}
        <Flex justify="center" mt={8} mb={4}>
          <Button
            colorPalette="purple"
            size="lg"
            borderRadius="xl"
            px={10}
            shadow="md"
            loading={submitting}
            loadingText="Enviando…"
            onClick={handleSubmit}><Icon asChild><FaCheck /></Icon>Enviar respuestas</Button>
        </Flex>
      </>
    );
  }

  return null;
};

// ═══════════════════════════════════════════════════════════════════════════
// PreguntaCard — renders a single question inside the form
// ═══════════════════════════════════════════════════════════════════════════
const ClickableImage = ({ src, alt, ...props }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <Image
        src={src} alt={alt} cursor="pointer"
        onClick={() => setOpen(true)}
        transition="transform 0.15s"
        _hover={{ transform: 'scale(1.03)', shadow: 'md' }}
        {...props}
      />
      <Dialog.Root open={open} size='xl' placement='center' onOpenChange={e => {
        if (!e.open) {
          setOpen(false);
        }
      }}>
        <Portal>

          <Dialog.Backdrop bg="blackAlpha.700" />
          <Dialog.Positioner>
            <Dialog.Content bg="transparent" shadow="none" maxW="90vw">
              <Dialog.CloseTrigger color="white" bg="blackAlpha.600" rounded="full" top={-3} right={-3} zIndex={2} />
              <Dialog.Body p={0} display="flex" justifyContent="center">
                <Image
                  src={src} alt={alt}
                  maxH="85vh" maxW="100%"
                  objectFit="contain"
                  borderRadius="lg"
                />
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>
    </>
  );
};

const PreguntaCard = React.memo(
  ({ pregunta, numero, respuesta, onUpdate, cardBg, accentColor }) => {
    const {
      id: preguntaId,
      texto,
      tipoPregunta,
      obligatoria,
      opciones = [],
      imagenes = [],
    } = pregunta;

    const handleTextoChange = (e) => {
      onUpdate(preguntaId, 'textoLibre', e.target.value);
    };

    const handleRadioChange = (d) => {
      onUpdate(preguntaId, 'opcionIds', d?.value ? [d.value] : []);
    };

    const handleCheckboxChange = (vals) => {
      const next = Array.isArray(vals) ? vals : vals?.value || [];
      onUpdate(preguntaId, 'opcionIds', next);
    };

    return (
      <Card.Root bg={cardBg} shadow="sm" borderRadius="xl" overflow="hidden">
        <Card.Body>
          <VStack gap={4} align="stretch">
            {/* Question header */}
            <HStack align="start" gap={3}>
              <Flex
                minW="32px" h="32px" borderRadius="full"
                bg="purple.100" align="center" justify="center"
                fontWeight="bold" fontSize="sm" color="purple.700"
              >
                {numero}
              </Flex>
              <Box flex="1">
                <HStack gap={2} align="baseline" flexWrap="wrap">
                  <Text fontWeight="semibold" fontSize="md">
                    {texto || pregunta.textoPregunta}
                  </Text>
                  {obligatoria && (
                    <Badge colorPalette="red" fontSize="2xs" variant="subtle">
                      obligatoria
                    </Badge>
                  )}
                </HStack>
              </Box>
            </HStack>

            {/* Question images */}
            {imagenes.length > 0 && (
              <SimpleGrid columns={{ base: 2, md: 3 }} gap={3}>
                {imagenes.map((img) => (
                  <ClickableImage
                    key={img.id}
                    src={img.url}
                    alt="Imagen de la pregunta"
                    borderRadius="lg"
                    objectFit="cover"
                    maxH="180px"
                    w="100%"
                  />
                ))}
              </SimpleGrid>
            )}

            <Separator />

            {/* Answer input */}
            {tipoPregunta === 'TEXTO_LIBRE' && (
              <Textarea
                placeholder="Escribe tu respuesta…"
                value={respuesta.textoLibre}
                onChange={handleTextoChange}
                borderRadius="lg"
                rows={3} />
            )}

            {tipoPregunta === 'SELECCION_UNICA' && (
              <RadioGroup.Root value={respuesta.opcionIds?.[0] || ''} onValueChange={handleRadioChange}>
                <Stack gap={3}>
                  {opciones.map((op) => (
                    <Box key={op.id}>
                      <Radio value={op.id} colorPalette="purple" size="md">
                        <Text fontSize="sm">{op.texto || op.textoOpcion}</Text>
                      </Radio>
                      {op.imagenes?.length > 0 && (
                        <SimpleGrid columns={2} gap={2} mt={2} ml={6}>
                          {op.imagenes.map((img) => (
                            <ClickableImage
                              key={img.id}
                              src={img.url}
                              alt="Imagen de opción"
                              borderRadius="md"
                              objectFit="cover"
                              maxH="120px"
                              w="100%"
                            />
                          ))}
                        </SimpleGrid>
                      )}
                    </Box>
                  ))}
                </Stack>
              </RadioGroup.Root>
            )}

            {tipoPregunta === 'SELECCION_MULTIPLE' && (
              <CheckboxGroup
                value={respuesta.opcionIds || []}
                onValueChange={handleCheckboxChange}
              >
                <Stack gap={3}>
                  {opciones.map((op) => (
                    <Box key={op.id}>
                      <Checkbox.Root value={op.id} colorPalette="purple" size="md"><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                        <Text fontSize="sm">{op.texto || op.textoOpcion}</Text>
                      </Checkbox.Label></Checkbox.Root>
                      {op.imagenes?.length > 0 && (
                        <SimpleGrid columns={2} gap={2} mt={2} ml={6}>
                          {op.imagenes.map((img) => (
                            <ClickableImage
                              key={img.id}
                              src={img.url}
                              alt="Imagen de opción"
                              borderRadius="md"
                              objectFit="cover"
                              maxH="120px"
                              w="100%"
                            />
                          ))}
                        </SimpleGrid>
                      )}
                    </Box>
                  ))}
                </Stack>
              </CheckboxGroup>
            )}
          </VStack>
        </Card.Body>
      </Card.Root>
    );
  },
);

PreguntaCard.displayName = 'PreguntaCard';

export default EncuestaConsultaPublica;
